/** Benign Chrome warning when ResizeObserver callbacks exceed one frame. */
export const RESIZE_OBSERVER_LOOP_RE = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i;

export function isBenignResizeObserverError(message) {
  if (typeof message === "string") return RESIZE_OBSERVER_LOOP_RE.test(message);
  if (message && typeof message.message === "string") {
    return RESIZE_OBSERVER_LOOP_RE.test(message.message);
  }
  return false;
}

/** Run layout-sensitive work after the browser finishes the current resize pass. */
export function scheduleAfterLayout(task, { settleMs = 0 } = {}) {
  let raf1 = 0;
  let raf2 = 0;
  let timeoutId = 0;
  raf1 = window.requestAnimationFrame(() => {
    raf2 = window.requestAnimationFrame(() => {
      if (settleMs > 0) {
        timeoutId = window.setTimeout(task, settleMs);
      } else {
        task();
      }
    });
  });
  return () => {
    window.cancelAnimationFrame(raf1);
    window.cancelAnimationFrame(raf2);
    if (timeoutId) window.clearTimeout(timeoutId);
  };
}

/**
 * Defer ResizeObserver callbacks out of the observation frame.
 * Prevents Chrome's "loop completed with undelivered notifications" during
 * React Flow remounts (view → edit) when sidebars change canvas size.
 */
export function patchResizeObserverToAvoidLoop() {
  if (typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
    return;
  }
  if (window.ResizeObserver.__sellyxPatched) return;

  const NativeRO = window.ResizeObserver;
  class PatchedResizeObserver extends NativeRO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (typeof callback === "function") callback(entries, observer);
        });
      });
    }
  }
  PatchedResizeObserver.__sellyxPatched = true;
  window.ResizeObserver = PatchedResizeObserver;
}

/** Prevent dev/prod error handlers from treating RO loop noise as a fatal crash. */
export function suppressBenignResizeObserverErrors() {
  const swallow = (event) => {
    const text = event?.message || event?.error?.message || event?.reason?.message;
    if (!isBenignResizeObserverError(text)) return false;
    event.stopImmediatePropagation?.();
    event.preventDefault?.();
    return true;
  };

  window.addEventListener("error", swallow, true);
  window.addEventListener("unhandledrejection", (event) => {
    if (swallow(event)) return;
  }, true);

  const previousOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (isBenignResizeObserverError(message) || isBenignResizeObserverError(error)) {
      return true;
    }
    if (typeof previousOnError === "function") {
      return previousOnError(message, source, lineno, colno, error);
    }
    return false;
  };
}
